/**
 * useActiveUsers Hook
 * FEA-133: Track and display active users in the application
 */

import { useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

interface ActiveUser {
  userId: string;
  userName: string;
  userEmail: string;
  lastSeen: string;
  /** Current page/route the user is viewing */
  currentPath: string;
  /** Human-readable page name */
  currentPageName: string;
}

/**
 * Map route paths to human-readable page names
 */
function getPageName(path: string): string {
  // Remove project ID from paths for cleaner display
  const cleanPath = path.replace(/\/projects\/[^/]+/, '/projects/:id');

  const pageNames: Record<string, string> = {
    '/': 'Dashboard',
    '/projects': 'Projects',
    '/projects/:id': 'Project',
    '/projects/:id/mission': 'Project Mission',
    '/projects/:id/components': 'Components',
    '/projects/:id/block-diagram': 'Block Diagram',
    '/projects/:id/functions': 'Functions',
    '/projects/:id/function-workshop': 'Function Workshop',
    '/projects/:id/failure-modes': 'Failure Modes',
    '/projects/:id/fm-workshop': 'FM Workshop',
    '/projects/:id/causes': 'Causes',
    '/projects/:id/causes-workshop': 'Causes Workshop',
    '/projects/:id/mitigations': 'Mitigations',
    '/projects/:id/mitigations-workshop': 'Mitigations Workshop',
    '/projects/:id/test-plans': 'Test Plans',
    '/projects/:id/risk-matrix': 'Risk Matrix',
    '/projects/:id/executive-dashboard': 'Executive Dashboard',
    '/projects/:id/reports': 'Reports',
  };

  // Try exact match first
  if (pageNames[cleanPath]) {
    return pageNames[cleanPath];
  }

  // Try partial matching for nested routes
  for (const [route, name] of Object.entries(pageNames)) {
    if (cleanPath.startsWith(route.replace(':id', ''))) {
      return name;
    }
  }

  return 'FMEA Studio';
}

// Query key
const activeUsersKey = ['active-users'] as const;

// API functions
async function getActiveUsers(): Promise<ActiveUser[]> {
  const res = await fetch('/api/user-presence');
  if (!res.ok) throw new Error('Failed to get active users');
  const json = await res.json();
  return json.data;
}

async function sendHeartbeat(
  userId: string,
  userName: string,
  userEmail: string,
  currentPath: string,
  currentPageName: string
): Promise<void> {
  await fetch('/api/user-presence/heartbeat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, userName, userEmail, currentPath, currentPageName }),
  });
}

async function clearPresence(userId: string): Promise<void> {
  await fetch(`/api/user-presence/${userId}`, {
    method: 'DELETE',
  });
}

/**
 * Hook to register current user presence and get list of other active users
 */
export function useActiveUsers() {
  const { user } = useAuth();
  const location = useLocation();
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();

  // Get current path and page name
  const currentPath = location.pathname;
  const currentPageName = getPageName(currentPath);

  // Fetch active users
  const { data: allUsers = [], isLoading } = useQuery({
    queryKey: activeUsersKey,
    queryFn: getActiveUsers,
    staleTime: 15000, // 15 seconds
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Filter out current user to get "other" users
  const otherUsers = allUsers.filter(u => u.userId !== user?.id);

  // BUG-003 FIX: Use refs for frequently-changing values to prevent interval accumulation
  const currentPathRef = useRef(currentPath);
  const currentPageNameRef = useRef(currentPageName);
  const userRef = useRef(user);

  // Keep refs updated
  useEffect(() => {
    currentPathRef.current = currentPath;
    currentPageNameRef.current = currentPageName;
    userRef.current = user;
  }, [currentPath, currentPageName, user]);

  // Register presence and start heartbeat when user is logged in
  useEffect(() => {
    if (!user?.id) return;

    // BUG-003 FIX: Clear any existing interval BEFORE creating new one
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    // Initial heartbeat
    sendHeartbeat(user.id, user.name, user.email, currentPath, currentPageName).catch((err) => {
      logger.error('Failed to send user heartbeat', err);
    });

    // Heartbeat every 60 seconds - use refs to get current values
    heartbeatIntervalRef.current = setInterval(() => {
      const u = userRef.current;
      if (!u?.id) return;
      sendHeartbeat(
        u.id,
        u.name,
        u.email,
        currentPathRef.current,
        currentPageNameRef.current
      ).catch((err) => {
        logger.error('Failed to send user heartbeat', err);
      });
      // Also refresh the active users list
      queryClient.invalidateQueries({ queryKey: activeUsersKey });
    }, 60 * 1000);

    // Cleanup: stop heartbeat
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      // Don't clear presence on path change, only on unmount
    };
  }, [user?.id]); // Only depend on user.id - other values accessed via refs

  // Send immediate heartbeat on path change (but don't recreate interval)
  useEffect(() => {
    if (!user?.id) return;
    sendHeartbeat(user.id, user.name, user.email, currentPath, currentPageName).catch((err) => {
      logger.error('Failed to send user heartbeat on path change', err);
    });
  }, [currentPath, currentPageName, user?.id, user?.name, user?.email]);

  // Clear presence on component unmount (logout/close)
  useEffect(() => {
    if (!user?.id) return;
    return () => {
      clearPresence(user.id).catch((err) => {
        logger.error('Failed to clear user presence', err);
      });
    };
  }, [user?.id]);

  // Also clear on window unload (browser close/refresh)
  useEffect(() => {
    if (!user?.id) return;

    const handleUnload = () => {
      // Use sendBeacon for reliability on page unload
      navigator.sendBeacon(
        `/api/user-presence/${user.id}`,
        JSON.stringify({ _method: 'DELETE' })
      );
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [user?.id]);

  // Get initials from name
  const getInitials = useCallback((name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }, []);

  // Generate a consistent color for a user based on their ID
  const getUserColor = useCallback((userId: string): string => {
    const colors = [
      '#3b82f6', // blue
      '#10b981', // emerald
      '#f59e0b', // amber
      '#ef4444', // red
      '#8b5cf6', // violet
      '#ec4899', // pink
      '#06b6d4', // cyan
      '#84cc16', // lime
    ];
    // Hash the userId to get a consistent index
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = ((hash << 5) - hash) + userId.charCodeAt(i);
      hash = hash & hash;
    }
    return colors[Math.abs(hash) % colors.length];
  }, []);

  return {
    otherUsers,
    allUsers,
    isLoading,
    getInitials,
    getUserColor,
  };
}

export default useActiveUsers;
